import {HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import {catchError, switchMap, throwError, timer, of} from 'rxjs';
import { AuthService } from '../services/auth';
import { EncryptionService } from '../services/encryption';
import { SETTINGS } from '../config/common.settings';
import { SubscriptionContextService } from '../services/subscription-context.service';
import { MessageService } from 'primeng/api';
import { PersistentErrorService } from '../services/persistent-error.service';
import {inject} from '@angular/core';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const encryptionService = inject(EncryptionService);
  const subscriptionContextService = inject(SubscriptionContextService);
  const messageService = inject(MessageService);
  const persistentErrorService = inject(PersistentErrorService);
  
  // console.log(`AuthInterceptor: Processing request to ${req.url}`);
  
  // Use a shared flag to prevent multiple simultaneous refresh attempts
  if (!(window as any).__isRefreshing) {
    (window as any).__isRefreshing = false;
  }
  
  // Track failed requests to avoid multiple refresh attempts
  if (!(window as any).__failedRequests) {
    (window as any).__failedRequests = new Set();
  }
  
  // Get the access token from storage
  const accessToken = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
  // console.log(`AuthInterceptor: Access token available: ${!!accessToken}`);
  
  // Get preferred language from localStorage
  const preferredLanguage = localStorage.getItem('preferredLanguage') || 'en';
  
  let newReq = req.clone({
    headers: req.headers
      .append('Accept', 'application/json')
      .set('Accept-Language', preferredLanguage),
    withCredentials: true
  });
  
  // Add authorization header if access token is available
  if (accessToken) {
    try {
      const decryptedToken = encryptionService.decrypt(accessToken);
      newReq = newReq.clone({
        headers: newReq.headers.set('Authorization', `Bearer ${decryptedToken}`)
      });
      // console.log(`AuthInterceptor: Authorization header added`);
    } catch (error) {
      console.error('Error decrypting access token:', error);
    }
  }

  // Add selected subscriber ID to request body for POST/PUT requests if available
  let selectedSubscriberId = subscriptionContextService.getSelectedSubscriberId();
  
  // Safeguard: Ensure selectedSubscriberId is a string, not an object
  if (selectedSubscriberId && typeof selectedSubscriberId === 'object') {
    console.warn('AuthInterceptor: selectedSubscriberId is an object, extracting _id');
    selectedSubscriberId = (selectedSubscriberId as any)._id || null;
  }
  
  if (selectedSubscriberId && typeof selectedSubscriberId === 'string') {
    // Skip file upload requests to avoid interfering with FormData
    const isFileUpload = req.url.includes('/files/upload');
    
    if ((req.method === 'POST' || req.method === 'PUT' || req.method === 'PATCH') && !isFileUpload) {
      try {
        const currentBody = req.body || {};
        const updatedBody = { ...currentBody, selectedSubscriberId };
        
        newReq = newReq.clone({
          body: updatedBody
        });
        
        // console.log(`AuthInterceptor: Added selectedSubscriberId to request body: ${selectedSubscriberId}`);
      } catch (error) {
        console.error('Error adding selectedSubscriberId to request body:', error);
      }
    } else if (req.method === 'GET') {
      try {
        // For GET requests, add as query parameter
        const currentParams = req.params;
        const updatedParams = currentParams.append('selectedSubscriberId', selectedSubscriberId);
        
        newReq = newReq.clone({
          params: updatedParams
        });
        
        // console.log(`AuthInterceptor: Added selectedSubscriberId to query params: ${selectedSubscriberId}`);
      } catch (error) {
        console.error('Error adding selectedSubscriberId to query params:', error);
      }
    }
  }
  
  return next(newReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // console.log(`AuthInterceptor: Request to ${req.url} failed with status ${error.status}`);
      
      if (req.url.includes('/login') || 
          req.url.includes('/verify-login-otp') || 
          req.url.includes('/verify-email-otp') ||
          req.url.includes('/users/refresh')) {
        // console.log(`AuthInterceptor: Skipping token refresh for auth endpoint: ${req.url}`);
        return throwError(() => error);
      }

      if (error.status === 403) {
        // Check if this is a permission error (not an authentication error)
        const errorBody = error.error;
        if (errorBody && (errorBody.error === 'Insufficient permissions' || errorBody.error === 'Access denied')) {
          console.log(`AuthInterceptor: Received 403 permission error: ${errorBody.message}`);
          
          // Show persistent permission error notification that survives route changes
          const requiredPermission = errorBody.requiredPermission || (errorBody.requiredPermissions && errorBody.requiredPermissions[0]);
          persistentErrorService.addPermissionError(
            errorBody.message || 'You don\'t have access to this resource. Please contact your administrator if you believe this is an error.',
            requiredPermission
          );
          
          // This is a permissions error, not an authentication error - don't refresh token
          return throwError(() => error);
        }
        
        const requestKey = `${req.method}:${req.url}`;
        // console.log(`AuthInterceptor: Received 403 authentication error for ${requestKey}, attempting token refresh...`);
        
        // Check if this request has already failed and we're already refreshing
        if ((window as any).__failedRequests.has(requestKey) && (window as any).__isRefreshing) {
          // console.log(`AuthInterceptor: Request ${requestKey} already failed, waiting for refresh to complete...`);
          return timer(1000).pipe(
            switchMap(() => {
              // console.log(`AuthInterceptor: Retrying ${requestKey} after wait...`);
              const latestTokenEnc = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
              let retriedReq = newReq;
              if (latestTokenEnc) {
                try {
                  const latestToken = encryptionService.decrypt(latestTokenEnc);
                  retriedReq = newReq.clone({
                    headers: newReq.headers.set('Authorization', `Bearer ${latestToken}`)
                  });
                } catch (error) {
                  console.error('Error decrypting token after wait:', error);
                }
              }
              return next(retriedReq);
            }),
            catchError((retryError) => {
              // If retry fails, check if it's a permissions error and show appropriate message
              if (retryError.status === 403) {
                const errorMessage = retryError.error?.message || 'You don\'t have access to this resource. Please contact your administrator if you believe this is an error.';
                messageService.add({
                  severity: 'error',
                  summary: 'Access Denied',
                  detail: errorMessage,
                  life: 5000
                });
              }
              return throwError(() => retryError);
            })
          );
        }
        
        if (!(window as any).__isRefreshing) {
          // console.log('AuthInterceptor: Starting token refresh...');
          (window as any).__isRefreshing = true;
          (window as any).__failedRequests.add(requestKey);
          
          return authService.refreshToken().pipe(
            switchMap(() => {
              // console.log('AuthInterceptor: Token refresh successful, retrying request...');
              (window as any).__isRefreshing = false;
              (window as any).__failedRequests.clear(); // Clear failed requests after successful refresh
              
              // Add a small delay to ensure token is properly stored and processed
              return timer(100).pipe(
                switchMap(() => {
                  // Rebuild request with the new access token
                  const latestTokenEnc = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
                  // console.log('AuthInterceptor: Latest token from storage:', !!latestTokenEnc);
                  
                  if (latestTokenEnc) {
                    try {
                      const latestToken = encryptionService.decrypt(latestTokenEnc);
                      // console.log('AuthInterceptor: Decrypted token length:', latestToken?.length);
                      
                      // Create a completely new request with the new token
                      const retriedReq = req.clone({
                        headers: req.headers
                          .set('Authorization', `Bearer ${latestToken}`)
                          .set('Accept', 'application/json'),
                        withCredentials: true
                      });
                      
                      // console.log('AuthInterceptor: Request rebuilt with new token, retrying...');
                      return next(retriedReq);
                    } catch (error) {
                      console.error('AuthInterceptor: Error decrypting refreshed token:', error);
                      // If we can't decrypt the token, something is wrong
                      // authService.logout();
                      return throwError(() => new Error('Failed to decrypt refreshed token'));
                    }
                  } else {
                    console.error('AuthInterceptor: No token found after refresh');
                    // authService.logout();
                    return throwError(() => new Error('No token found after refresh'));
                  }
                })
              );
            }),
            catchError((refreshError) => {
              console.error('AuthInterceptor: Token refresh failed:', refreshError);
              (window as any).__isRefreshing = false;
              (window as any).__failedRequests.clear();
              
              // Only logout if refresh token is actually expired (401/403)
              if (refreshError.status === 401 || refreshError.status === 403) {
                console.log('AuthInterceptor: Refresh token expired, logging out...');
                authService.logout();
              } else {
                // Show error notification for other refresh failures
                messageService.add({
                  severity: 'error',
                  summary: 'Authentication Error',
                  detail: 'Failed to refresh authentication. Please try again.',
                  life: 5000
                });
              }
              
              return throwError(() => refreshError);
            })
          );
        } else {
          console.log('AuthInterceptor: Token refresh already in progress, marking request as failed...');
          (window as any).__failedRequests.add(requestKey);
          
          // If already refreshing, wait a bit and retry the original request
          return timer(1000).pipe(
            switchMap(() => {
              console.log(`AuthInterceptor: Retrying ${requestKey} after wait...`);
              const latestTokenEnc = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
              
              if (latestTokenEnc) {
                try {
                  const latestToken = encryptionService.decrypt(latestTokenEnc);
                  console.log(`AuthInterceptor: Decrypted token length after wait:`, latestToken?.length);
                  
                  // Create a completely new request with the new token
                  const retriedReq = req.clone({
                    headers: req.headers
                      .set('Authorization', `Bearer ${latestToken}`)
                      .set('Accept', 'application/json'),
                    withCredentials: true
                  });
                  
                  return next(retriedReq);
                } catch (error) {
                  console.error('AuthInterceptor: Error decrypting token after wait:', error);
                  return throwError(() => new Error('Failed to decrypt token after wait'));
                }
              } else {
                console.error('AuthInterceptor: No token found after wait');
                return throwError(() => new Error('No token found after wait'));
              }
            })
          );
        }
      }

      // Handle other 403 errors (not during token refresh)
      if (error.status === 403) {
        const errorMessage = error.error?.message || 'You don\'t have access to this resource. Please contact your administrator if you believe this is an error.';
        messageService.add({
          severity: 'error',
          summary: 'Access Denied',
          detail: errorMessage,
          life: 5000
        });
      }

      console.log(`AuthInterceptor: Returning error for ${req.url}:`, error);
      return throwError(() => error);
    })
  );
};

